import { toast } from "sonner";

/**
 * The "you got a message" toast card, shared by internal chat, WhatsApp and a
 * silent alert so all three look and behave identically. Rendered via
 * `toast.custom` because sonner's stock variants can't show an avatar + a
 * two-line preview.
 */

export type MessageToastVariant = "internal" | "whatsapp" | "alert";

export interface NewMessageToastOptions {
  /** Who sent it — a teammate's name, or the contact's name for WhatsApp. */
  senderName: string;
  /** A heading above the preview (an alert's title). */
  title?: string;
  /** One-line body preview, already truncated by the caller. */
  preview: string;
  /** Renders the "@you" pill (internal chat mentions). */
  mentioned?: boolean;
  variant?: MessageToastVariant;
  /** How long it stays, in ms. A chat line reads in 5 s; an alert gets longer. */
  duration?: number;
  /** Runs on click, after the toast is dismissed (e.g. open the thread). */
  onClick?: () => void;
}

const ACCENT: Record<
  MessageToastVariant,
  { bg: string; fg: string; dot: string; pill?: string }
> = {
  internal: { bg: "#eaf5f1", fg: "#218760", dot: "#218760" },
  whatsapp: { bg: "#e7f7ee", fg: "#1da851", dot: "#25d366", pill: "WHATSAPP" },
  alert: { bg: "#fff4ec", fg: "#ff8447", dot: "#ff8447", pill: "ALERT" },
};

function initials(name: string) {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function showNewMessageToast({
  senderName,
  title,
  preview,
  mentioned = false,
  variant = "internal",
  duration = 5000,
  onClick,
}: NewMessageToastOptions) {
  const accent = ACCENT[variant];

  toast.custom(
    (id) => (
      <div
        className="flex items-start gap-3 px-4 py-3 bg-white rounded-xl shadow-lg border border-[#e6e8e7] max-w-[360px] cursor-pointer hover:bg-[#f7f9f8] transition-colors"
        onClick={() => {
          toast.dismiss(id);
          onClick?.();
        }}
      >
        <div
          className="size-9 rounded-full flex items-center justify-center shrink-0 mt-0.5"
          style={{ backgroundColor: accent.bg }}
        >
          <span
            className="text-[11px] font-semibold"
            style={{ color: accent.fg }}
          >
            {initials(senderName)}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-[13px] font-semibold text-[#1e302a] truncate">
              {senderName}
            </p>
            {accent.pill && (
              <span
                className="px-1.5 py-0.5 rounded-full text-white text-[8px] font-bold"
                style={{ backgroundColor: accent.dot }}
              >
                {accent.pill}
              </span>
            )}
            {mentioned && (
              <span className="px-1.5 py-0.5 rounded-full bg-[#335CFF] text-white text-[8px] font-bold">
                @you
              </span>
            )}
          </div>
          {title && (
            <p className="text-[12px] font-medium text-[#1e302a] truncate mt-0.5">
              {title}
            </p>
          )}
          <p className="text-[12px] text-[#697571] truncate mt-0.5">{preview}</p>
        </div>
        <div className="shrink-0 mt-0.5">
          <div
            className="size-2 rounded-full"
            style={{ backgroundColor: accent.dot }}
          />
        </div>
      </div>
    ),
    { duration, position: "top-right" },
  );
}
